import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import WizardService from '../../../services/wizardService';

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [caseRec, setCaseRec] = useState(null);
  const [stage, setStage] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [renameDrafts, setRenameDrafts] = useState({}); // { path: { editing: bool, name: string } }
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'todo',
    due_date: '',
    assigned_to: ''
  });
  const [staff, setStaff] = useState([]);
  const [controlStatus, setControlStatus] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = async () => {
    const { data: t } = await supabase.from('case_tasks').select('*').eq('id', id).single();
    setTask(t || null);
    setForm({
      title: t?.title || '',
      description: t?.description || '',
      status: t?.status || 'todo',
      due_date: t?.due_date || '',
      assigned_to: t?.assigned_to || ''
    });
    if (t?.case_id) {
      const { data: c } = await supabase.from('cases').select('*').eq('id', t.case_id).single();
      setCaseRec(c || null);
      const { data: staffRows } = await supabase.from('staff_profiles').select('id,name,email').order('name');
      setStaff(staffRows || []);
    }
    if (t?.stage_id) {
      const { data: s } = await supabase.from('case_stages').select('*').eq('id', t.stage_id).single();
      setStage(s || null);
    }
    if (t?.case_id) {
      await loadAttachments(t.case_id, t.id);
    }

    // Control Room status (deadline awareness) for the task itself
    try {
      const { data: deadlineRow } = await supabase
        .from('unified_deadlines')
        .select('deadline, deadline_type')
        .eq('entity_id', id)
        .eq('deadline_type', 'task')
        .single();
      if (deadlineRow?.deadline) {
        const deadlineDate = new Date(deadlineRow.deadline);
        const now = new Date();
        const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
        let level = 'attention';
        if (deadlineDate < now || diffDays <= 0) level = 'critical';
        else if (diffDays <= 3) level = 'urgent';
        setControlStatus({
          level,
          deadline: deadlineDate,
          days: diffDays
        });
      } else {
        setControlStatus(null);
      }
    } catch (deadlineErr) {
      console.warn('Task control status lookup failed', deadlineErr);
      setControlStatus(null);
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveTask = async () => {
    if (!task) return;
    const updates = {
      title: form.title,
      description: form.description,
      status: form.status,
      due_date: form.due_date || null,
      assigned_to: form.assigned_to || null
    };
    try {
      setSaving(true);
      await supabase.from('case_tasks').update(updates).eq('id', id);
      await load();
      setToast({ type: 'success', message: 'Task saved' });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to save task' });
    } finally {
      setSaving(false);
    }
  };

  const loadAttachments = async (caseId, taskId) => {
    try {
      // Prefer DB entries if available
      const { data: rows } = await supabase
        .from('case_attachments')
        .select('file_name,file_path,file_size')
        .eq('task_id', taskId);
      if (rows && Array.isArray(rows)) {
        const withUrls = [];
        for (const r of rows) {
          const { data: signed } = await supabase.storage.from('documents').createSignedUrl(r.file_path, 600);
          withUrls.push({ name: r.file_name, path: r.file_path, size: r.file_size || 0, url: signed?.signedUrl || null });
        }
        setAttachments(withUrls);
        return;
      }
      // Fallback to listing storage if table not present
      const baseDir = `cases/${caseId}/tasks/${taskId}`;
      const { data: list } = await supabase.storage.from('documents').list(baseDir, { limit: 100 });
      const items = list || [];
      const withUrls = [];
      for (const it of items) {
        const fullPath = `${baseDir}/${it.name}`;
        const { data: signed } = await supabase.storage.from('documents').createSignedUrl(fullPath, 600);
        withUrls.push({ name: it.name, path: fullPath, size: it.metadata?.size || it.size || 0, url: signed?.signedUrl || null });
      }
      setAttachments(withUrls);
    } catch (e) {
      setAttachments([]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFiles.length || !task?.case_id) return;
    try {
      setUploading(true);
      setUploadProgress(0);
      const step = Math.max(1, Math.floor(100 / uploadFiles.length));
      for (const file of uploadFiles) {
        const path = `cases/${task.case_id}/tasks/${task.id}/${Date.now()}_${file.name}`;
        const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
        if (!upErr) {
          const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);
          await supabase.from('case_notes').insert([{ case_id: task.case_id, content: `Task ${task.id} attachment: ${pub?.publicUrl || path} (${file.name})` }]);
          // Insert into case_attachments (best-effort)
          await supabase.from('case_attachments').insert([
            { case_id: task.case_id, task_id: task.id, file_path: path, file_name: file.name, file_size: file.size }
          ]).catch(() => { });
        }
        setUploadProgress((prev) => Math.min(100, prev + step));
      }
      setUploadProgress(100);
      setUploadFiles([]);
      await loadAttachments(task.case_id, task.id);
      setToast({ type: 'success', message: 'Attachment(s) uploaded' });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to upload attachment(s)' });
    } finally {
      setUploading(false);
    }
  };

  const deleteAttachment = async (att) => {
    if (!task?.case_id) return;
    const confirmed = window.confirm(`Delete file "${att.name}"?`);
    if (!confirmed) return;
    try {
      const { error } = await supabase.storage.from('documents').remove([att.path]);
      if (error) throw error;
      await supabase.from('case_notes').insert([{ case_id: task.case_id, content: `Task ${task.id} attachment deleted: ${att.path}` }]);
      await supabase.from('case_attachments').delete().eq('task_id', task.id).eq('file_path', att.path).catch(() => { });
      await loadAttachments(task.case_id, task.id);
      setToast({ type: 'success', message: 'Attachment deleted' });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to delete attachment' });
    }
  };

  const startRename = (att) => {
    setRenameDrafts((prev) => ({ ...prev, [att.path]: { editing: true, name: att.name } }));
  };
  const cancelRename = (att) => {
    setRenameDrafts((prev) => ({ ...prev, [att.path]: { editing: false, name: att.name } }));
  };
  const saveRename = async (att) => {
    const draft = renameDrafts[att.path];
    if (!draft?.name || draft.name === att.name) { cancelRename(att); return; }
    // sanitize name (basic): remove slashes
    const baseDir = att.path.slice(0, att.path.lastIndexOf('/'));
    const newPath = `${baseDir}/${draft.name.replace(/[\\/]/g, '-')}`;
    try {
      const { error } = await supabase.storage.from('documents').move(att.path, newPath);
      if (error) throw error;
      await supabase.from('case_notes').insert([{ case_id: task.case_id, content: `Task ${task.id} attachment renamed: ${att.name} → ${draft.name}` }]);
      await supabase.from('case_attachments').update({ file_name: draft.name, file_path: newPath }).eq('task_id', task.id).eq('file_path', att.path).catch(() => { });
      await loadAttachments(task.case_id, task.id);
      setToast({ type: 'success', message: 'Attachment renamed' });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to rename attachment' });
    } finally {
      setRenameDrafts((prev) => ({ ...prev, [att.path]: { editing: false, name: draft?.name || att.name } }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg border ${toast.type === 'success' ? 'bg-green-900/90 border-green-700 text-green-100' : 'bg-red-900/90 border-red-700 text-red-100'
          }`}>
          {toast.message}
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                Task Details
                {task && (
                  <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider border ${task.status === 'done' ? 'bg-green-900/30 text-green-400 border-green-800' :
                      task.status === 'in_progress' ? 'bg-blue-900/30 text-blue-400 border-blue-800' :
                        'bg-yellow-900/30 text-yellow-400 border-yellow-800'
                    }`}>
                    {task.status.replace('_', ' ')}
                  </span>
                )}
              </h1>
              <p className="text-gray-400 text-xs mt-1">ID: {id} • {task?.case_id ? `Case #${task.case_id}` : 'General Task'}</p>
              {controlStatus && (
                <div className="mt-1 text-xs flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded border ${
                    controlStatus.level === 'critical' ? 'bg-red-900/30 text-red-300 border-red-800' :
                    controlStatus.level === 'urgent' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' :
                    'bg-blue-900/30 text-blue-300 border-blue-800'
                  }`}>
                    {controlStatus.level.toUpperCase()}
                  </span>
                  <span className="text-gray-400">
                    {controlStatus.deadline ? controlStatus.deadline.toLocaleDateString() : 'No deadline'}
                    {typeof controlStatus.days === 'number' ? ` (${controlStatus.days}d)` : ''}
                  </span>
                </div>
              )}
            </div>
            </div>

          <div className="flex items-center gap-3">
            <button
              onClick={saveTask}
              disabled={saving}
              className="px-4 py-2 rounded bg-blue-700 text-white hover:bg-blue-600 border border-blue-600 text-sm font-bold uppercase tracking-wider transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            {task?.case_id && (
              <button
                onClick={() => navigate(`/admin/cases/${task.case_id}`)}
                className="px-4 py-2 rounded bg-gray-800 text-blue-400 hover:bg-gray-700 border border-gray-700 text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
              >
                <span>View Case</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {!task ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <div className="text-gray-400 animate-pulse">Loading task details...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Task Title</label>
                    <input
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      value={form.title}
                      onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                      disabled={saving}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Description</label>
                    <textarea
                      rows="8"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-300 placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all leading-relaxed"
                      placeholder="Add detailed description, checklist, or notes..."
                      value={form.description}
                      onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Attachments Section */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/50">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attachments
                    <span className="bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded-full">{attachments.length}</span>
                  </h3>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center gap-3">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center w-full h-12 px-4 transition bg-gray-900 border-2 border-gray-700 border-dashed rounded-lg hover:border-gray-500 hover:bg-gray-800">
                          <div className="flex items-center space-x-2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span className="text-sm text-gray-400 font-medium">
                              {uploadFiles.length > 0 ? `${uploadFiles.length} files selected` : 'Click to upload files'}
                            </span>
                          </div>
                          <input type="file" multiple className="hidden" onChange={(e) => setUploadFiles(Array.from(e.target.files || []))} />
                        </div>
                      </label>
                      <button
                        onClick={handleUpload}
                        disabled={uploading || !uploadFiles.length}
                        className="h-12 px-6 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-900/20"
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </button>
                    </div>

                    {uploading && (
                      <div className="mt-3 h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {attachments.map((f, idx) => (
                      <div key={idx} className="group flex items-center justify-between p-3 bg-gray-900/50 border border-gray-700 rounded-lg hover:border-gray-600 transition-colors">
                        <div className="flex items-center gap-3 overflow-hidden">
                          <div className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center text-gray-400 border border-gray-700">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            {renameDrafts[f.path]?.editing ? (
                              <div className="flex items-center gap-2">
                                <input
                                  className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:ring-1 focus:ring-blue-500"
                                  value={renameDrafts[f.path]?.name || ''}
                                  onChange={(e) => setRenameDrafts(prev => ({ ...prev, [f.path]: { ...(prev[f.path] || {}), name: e.target.value } }))}
                                  autoFocus
                                />
                                <button onClick={() => saveRename(f)} className="text-green-400 hover:text-green-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                                <button onClick={() => cancelRename(f)} className="text-red-400 hover:text-red-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                              </div>
                            ) : (
                              <>
                                <div className="text-sm font-medium text-gray-200 truncate" title={f.name}>{f.name}</div>
                                <div className="text-xs text-gray-500">{(f.size / 1024).toFixed(1)} KB</div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {f.url && (
                            <a
                              href={f.url}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 rounded hover:bg-gray-700 text-blue-400 hover:text-blue-300 transition-colors"
                              title="Download"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </a>
                          )}
                          {!renameDrafts[f.path]?.editing && (
                            <button
                              onClick={() => startRename(f)}
                              className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                              title="Rename"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => deleteAttachment(f)}
                            className="p-2 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    {attachments.length === 0 && (
                      <div className="text-center py-8 text-gray-500 text-sm italic">No attachments yet</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Meta Info */}
            <div className="space-y-6">
              <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50">
                  <h3 className="font-bold text-white text-sm uppercase tracking-wider">Properties</h3>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Status</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.status}
                      onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                      disabled={saving}
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Due Date</label>
                    <input
                      type="date"
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.due_date || ''}
                      onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                      disabled={saving}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Assigned To</label>
                    <select
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.assigned_to || ''}
                      onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                      disabled={saving}
                    >
                      <option value="">Unassigned</option>
                      {staff.map(s => (
                        <option key={s.id} value={s.email}>{s.name || s.email}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Stage</label>
                    <div className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-gray-300 text-sm">
                      {stage?.title || 'No Stage'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden p-6">
                <div className="text-xs text-gray-500 font-mono space-y-2">
                  <div className="flex justify-between">
                    <span>Created:</span>
                    <span className="text-gray-400">{new Date(task.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Updated:</span>
                    <span className="text-gray-400">{new Date(task.updated_at || task.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailPage;
